import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { FleetDashboardFilters } from 'models/fleet-management/FleetDataFilter';
import { FleetDataReliabilityWeek } from 'models/fleet-management/FleetDataSet';
import { useFleetInsights } from 'hooks/useFleetDataset';
import { CHART_SERIES_BLUE } from 'utils/fleet-dashboard-theme';
import {
  ReliabilityPoint,
  TREND_WEEKS,
  reliabilitySeries,
  trendEnds,
} from 'utils/performance-changes';
import useTrendQuery from 'hooks/useTrendQuery';
import DashboardSection from '../DashboardSection';
import DatasetState from './DatasetState';
import { CardGridSkeleton } from './SectionSkeletons';

/** A metric card's height: its label, figure, starting value and sparkline. */
export const RELIABILITY_CARD_HEIGHT = 204;

const RELIABILITY_METRICS: {
  key: keyof Omit<ReliabilityPoint, 'week'>;
  labelKey: string;
  format: (value: number) => string;
}[] = [
  {
    key: 'mtbf',
    labelKey: 'fleet-management-reliability-mtbf',
    format: (v) => `${Math.round(v)} h`,
  },
  {
    key: 'failureRatio',
    labelKey: 'fleet-management-reliability-failure-ratio',
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'successRate',
    labelKey: 'fleet-management-reliability-success-rate',
    format: (v) => `${Math.round(v)}%`,
  },
];

/** MTBF, failure ratio and success rate over the trend window, each with its weekly line. */
const ReliabilitySection: React.FC<{ filters: FleetDashboardFilters }> = ({ filters }) => {
  const { t } = useTranslation();
  const { trendQuery, grid } = useTrendQuery(filters);

  const reliability = useFleetInsights<FleetDataReliabilityWeek[]>('reliability', trendQuery);

  const series = useMemo(
    () => reliabilitySeries(reliability.data ?? [], grid),
    [grid, reliability.data],
  );

  return (
    <DashboardSection
      title={t('fleet-management-title-reliability', { count: TREND_WEEKS })}
      caption={t('fleet-management-caption-reliability')}
      info={t('fleet-management-info-reliability')}
    >
      <DatasetState
        isLoading={reliability.isLoading}
        hasError={reliability.hasError}
        hasData={reliability.data != null}
        skeleton={
          <CardGridSkeleton
            count={RELIABILITY_METRICS.length}
            height={RELIABILITY_CARD_HEIGHT}
            item={{ xs: 12, md: 4 }}
          />
        }
      >
        <Grid container spacing={2}>
          {RELIABILITY_METRICS.map((metric) => {
            const values = series.map((point) => point[metric.key]);
            const { first, last } = trendEnds(values);
            return (
              <Grid item xs={12} md={4} key={metric.key}>
                <Stack
                  role="group"
                  aria-label={t(metric.labelKey)}
                  gap={1}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {t(metric.labelKey)}
                  </Typography>
                  <Typography variant="h4">{last == null ? '—' : metric.format(last)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {first == null
                      ? t('fleet-management-no-data')
                      : t('fleet-management-reliability-from', { value: metric.format(first) })}
                  </Typography>
                  <SparkLineChart
                    // A week without a mission is a gap in the line, not a zero.
                    data={values as number[]}
                    height={64}
                    area
                    curve="linear"
                    colors={[CHART_SERIES_BLUE]}
                    margin={{ top: 6, bottom: 4, left: 2, right: 2 }}
                    sx={{ '.MuiAreaElement-root': { fillOpacity: 0.15 } }}
                  />
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </DatasetState>
    </DashboardSection>
  );
};

export default ReliabilitySection;
